/**
 * Where the sun is: NOAA's solar position algorithm (the NOAA Solar Calculator, after Meeus) with NOAA's refraction,
 * the same as the backend's `analysis/sun.py`; within about 0.01° of NREL's SPA (tested against its values).
 *
 * The editor works in solar time (noon = the sun at its highest, due south or north): the site's time zone is not
 * known, and sun path diagrams are drawn in solar time anyway. `solarToUtc` converts when a clock time is needed.
 */

const RAD = Math.PI / 180;

export interface SunPosition {
  /** Degrees from north, clockwise. */
  azimuth: number;
  /** Degrees above the horizon, refracted. */
  elevation: number;
}

/** Julian day of a UTC instant (ms since 1970). */
export function julianDay(ms: number): number {
  return ms / 86400000 + 2440587.5;
}

/** Declination (degrees) and the equation of time (minutes) at a Julian day. */
export function solarTerms(jd: number): { declination: number; eot: number } {
  const t = (jd - 2451545) / 36525;
  const l0 = (((280.46646 + t * (36000.76983 + t * 0.0003032)) % 360) + 360) % 360;
  const m = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const mr = m * RAD;
  const c = Math.sin(mr) * (1.914602 - t * (0.004817 + 0.000014 * t)) + Math.sin(2 * mr) * (0.019993 - 0.000101 * t) + Math.sin(3 * mr) * 0.000289;
  const omega = 125.04 - 1934.136 * t;
  const apparent = l0 + c - 0.00569 - 0.00478 * Math.sin(omega * RAD);
  const eps0 = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const eps = (eps0 + 0.00256 * Math.cos(omega * RAD)) * RAD;
  const declination = Math.asin(Math.sin(eps) * Math.sin(apparent * RAD)) / RAD;
  const y = Math.tan(eps / 2) ** 2;
  const l0r = l0 * RAD;
  const eot =
    4 / RAD *
    (y * Math.sin(2 * l0r) - 2 * e * Math.sin(mr) + 4 * e * y * Math.sin(mr) * Math.cos(2 * l0r) - 0.5 * y * y * Math.sin(4 * l0r) - 1.25 * e * e * Math.sin(2 * mr));
  return { declination, eot };
}

/** NOAA's refraction correction (degrees to add to the geometric elevation). */
export function refraction(h: number): number {
  if (h > 85) return 0;
  const t = Math.tan(h * RAD);
  let arcsec: number;
  if (h > 5) arcsec = 58.1 / t - 0.07 / t ** 3 + 0.000086 / t ** 5;
  else if (h > -0.575) arcsec = 1735 + h * (-518.2 + h * (103.4 + h * (-12.79 + h * 0.711)));
  else arcsec = -20.772 / t;
  return arcsec / 3600;
}

/** Azimuth and elevation from latitude, declination and hour angle (degrees; hour angle 0 at solar noon). */
export function horizontal(lat: number, declination: number, hourAngle: number, refract = true): SunPosition {
  const la = lat * RAD;
  const d = declination * RAD;
  const ha = hourAngle * RAD;
  const cosZen = Math.min(1, Math.max(-1, Math.sin(la) * Math.sin(d) + Math.cos(la) * Math.cos(d) * Math.cos(ha)));
  const zen = Math.acos(cosZen);
  let elevation = 90 - zen / RAD;
  const denom = Math.cos(la) * Math.sin(zen);
  const cosAz = Math.abs(denom) < 1e-9 ? 1 : (Math.sin(la) * cosZen - Math.sin(d)) / denom;
  const az = Math.acos(Math.min(1, Math.max(-1, cosAz))) / RAD;
  const azimuth = hourAngle > 0 ? (az + 180) % 360 : (540 - az) % 360;
  if (refract) elevation += refraction(elevation);
  return { azimuth, elevation };
}

/** The sun at a UTC instant (ms) seen from (lat, lng). */
export function sunPosition(ms: number, lat: number, lng: number): SunPosition {
  const { declination, eot } = solarTerms(julianDay(ms));
  const utcMinutes = (((ms / 60000) % 1440) + 1440) % 1440;
  const trueSolar = (((utcMinutes + eot + 4 * lng) % 1440) + 1440) % 1440;
  return horizontal(lat, declination, trueSolar / 4 - 180);
}

/** Julian day at noon of a day of the year (1-366) in `year`. */
export function dayJulian(dayOfYear: number, year = 2026): number {
  return julianDay(Date.UTC(year, 0, 1, 12)) + dayOfYear - 1;
}

/** The sun on a day of the year at a solar time (minutes after solar midnight). */
export function sunAtSolarTime(lat: number, dayOfYear: number, solarMinutes: number, year = 2026): SunPosition {
  const { declination } = solarTerms(dayJulian(dayOfYear, year));
  return horizontal(lat, declination, solarMinutes / 4 - 180);
}

/** The UTC instant (ms) of a solar time on a day at longitude `lng` (east positive). */
export function solarToUtc(dayOfYear: number, solarMinutes: number, lng: number, year = 2026): number {
  const { eot } = solarTerms(dayJulian(dayOfYear, year));
  const utcMinutes = solarMinutes - eot - 4 * lng;
  return Date.UTC(year, 0, 1) + (dayOfYear - 1) * 86400000 + utcMinutes * 60000;
}

/** Sunrise and sunset in solar minutes (null: polar day or night), from the geometric -0.833° horizon. */
export function dayLength(lat: number, dayOfYear: number, year = 2026): { rise: number; set: number } | null {
  const { declination } = solarTerms(dayJulian(dayOfYear, year));
  const cosH = (Math.sin(-0.833 * RAD) - Math.sin(lat * RAD) * Math.sin(declination * RAD)) / (Math.cos(lat * RAD) * Math.cos(declination * RAD));
  if (cosH <= -1 || cosH >= 1) return null;
  const h = Math.acos(cosH) / RAD;
  return { rise: 720 - 4 * h, set: 720 + 4 * h };
}

/** Unit vector to the sun in local ENU (east, north, up). */
export function sunVector({ azimuth, elevation }: SunPosition): [number, number, number] {
  const a = azimuth * RAD;
  const e = elevation * RAD;
  return [Math.sin(a) * Math.cos(e), Math.cos(a) * Math.cos(e), Math.sin(e)];
}

/** Day of the year (1-366) of a month (0-11) and day of the month. */
export function dayOfYear(month: number, day: number, year = 2026): number {
  return Math.round((Date.UTC(year, month, day) - Date.UTC(year, 0, 1)) / 86400000) + 1;
}

/** The UTC date of a day of the year (format it with Intl: `{ day: "numeric", month: "short", timeZone: "UTC" }`). */
export function dayDate(doy: number, year = 2026): Date {
  return new Date(Date.UTC(year, 0, 1) + (doy - 1) * 86400000);
}

/** Solar minutes as a UTC clock time on 1 Jan 1970 (format it with Intl and `timeZone: "UTC"`). */
export function solarClock(minutes: number): Date {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return new Date(m * 60000);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTH_NAMES = MONTHS;

/** "21 Jun" for a day of the year (English, for tests and logs; the UI formats `dayDate` with Intl). */
export function formatDay(doy: number, year = 2026): string {
  const d = new Date(Date.UTC(year, 0, 1) + (doy - 1) * 86400000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "14:30" for solar minutes. */
export function formatSolarTime(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Points of the sun's track on a day (above the horizon), every `stepMin` solar minutes. */
export function dayPath(lat: number, doy: number, stepMin = 10): (SunPosition & { minutes: number })[] {
  const out: (SunPosition & { minutes: number })[] = [];
  for (let m = 0; m <= 1440; m += stepMin) {
    const p = sunAtSolarTime(lat, doy, m);
    if (p.elevation > -0.5) out.push({ ...p, minutes: m });
  }
  return out;
}

/** The analemma of one solar hour over the year (the hour lines of a sun path diagram). */
export function hourLine(lat: number, solarHour: number): SunPosition[] {
  const out: SunPosition[] = [];
  for (let doy = 1; doy <= 365; doy += 7) {
    const p = sunAtSolarTime(lat, doy, solarHour * 60);
    if (p.elevation > -0.5) out.push(p);
  }
  return out;
}
